import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ChatMessageProgressService } from './chat-message-progress-bar.service';
import environment from '../../environments/environment';
import { ChatResponseDto } from '../dtos/chat-response.dto';
import { DeleteFilesResponse } from '../interfaces/file-upload.interface';
import ChatApi from '../shared/interfaces/chat-api.interface';
import { MessageDataview } from '../shared/interfaces/message-dataview.interface';

@Injectable({ providedIn: 'root' })
export class TaapService implements ChatApi {
    /**
     *
     * @param http
     * @param progressService
     */
    constructor(
        private http: HttpClient,
        private progressService: ChatMessageProgressService
    ) {}

    /**
     * Get chat answer for a prompt with 60-second timeout protection
     * @param history - Chat history
     * @param conversationId - Conversation ID
     * @param hideSources - Hide sources flag
     * @param clientId - Client identifier
     * @param regenerate - Whether to regenerate
     */
    getChatAnswer(
        history: any[],
        conversationId: string,
        hideSources = false,
        clientId?,
        regenerate = false
    ): Observable<ChatResponseDto> {
        const lastyHistoryElement = [history[history.length - 1]];
        this.progressService.start();
        return this.http.post<ChatResponseDto>(
            `${environment.apiBaseUrl}/taap-chat?clientId=${clientId}`,
            {
                conversationId,
                history: lastyHistoryElement,
                hideSources,
                regenerate,
            }
        );
    }

    /**
     *
     * @param prompt
     * @param metadataValue
     * @param conversationId
     * @param consent
     * @param metaData
     */
    getAnswerFromInternet(
        prompt: string,
        conversationId: string,
        consent: 'yes' | 'no',
        metaData = {}
    ) {
        this.progressService.start();
        return this.http.post<MessageDataview>(`${environment.apiBaseUrl}/taap/internetSearch`, {
            query: prompt,
            conversationId,
            consent,
            metaData,
        });
    }

    /**
     *
     * @param prompt
     * @param metadataValue
     * @param clientId
     */
    searchAndResults(prompt: string, metadataValue, clientId) {
        // TODO: we have to set parameters properly
        let request_ids = [];

        if (metadataValue.request_ids) {
            request_ids = [...metadataValue.request_ids];
            delete metadataValue.request_id;
            delete metadataValue.request_ids;
        }
        this.progressService.start();
        return this.http.post<MessageDataview>(
            `${environment.apiBaseUrl}/taap/prepare?clientId=${clientId}`,
            {
                query: prompt,
                request_ids,
                filters: metadataValue,
            }
        );
    }

    /**
     *
     * @param file
     * @param isTemporary
     */
    uploadDocument(file: File, isTemporary = false): Observable<HttpEvent<any>> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isTemporary', String(isTemporary));

        const req = new HttpRequest('POST', `${environment.apiBaseUrl}/taap/upload`, formData, {
            reportProgress: true,
        });

        return this.http.request(req);
    }

    /**
     *
     * @param referenceId
     */
    getUploadSummary(referenceId: string): Observable<HttpEvent<any>> {
        const req = new HttpRequest(
            'GET',
            `${environment.apiBaseUrl}/taap/upload/summary/${referenceId}`
        );
        return this.http.request(req);
    }

    /**
     *
     * @param referenceId
     */
    cancelUpload(referenceId: string): Observable<HttpEvent<any>> {
        const req = new HttpRequest(
            'DELETE',
            `${environment.apiBaseUrl}/taap/upload/${referenceId}/cancel`
        );
        return this.http.request(req);
    }

    /**
     *
     */
    getFilesUpload(): Observable<HttpEvent<any>> {
        const req = new HttpRequest(
            'GET',
            `${environment.apiBaseUrl}/taap-chat/conversation/files/list`
        );
        return this.http.request(req);
    }

    /**
     *
     * @param fileIds
     */
    deleteConversationFiles(fileIds: string[]): Observable<DeleteFilesResponse> {
        return this.http.post<DeleteFilesResponse>(
            `${environment.apiBaseUrl}/taap-chat/conversation/files/delete`,
            { requestIds: fileIds }
        );
    }

    /**
     * Extends the storage period of a saved file.
     * @param fileId
     */
    extendFileRetention(
        fileId: string
    ): Observable<{ id: string; expiresAt: string; extensionCount: number; canExtend: boolean }> {
        return this.http.post<{
            id: string;
            expiresAt: string;
            extensionCount: number;
            canExtend: boolean;
        }>(
            `${environment.apiBaseUrl}/taap-chat/conversation/files/${encodeURIComponent(fileId)}/extend`,
            {}
        );
    }
}
